/**
 * ResendVerificationButton — Q119-Q121 protocol №030.
 *
 * 5 visual states (per AD voice rules):
 *   idle             — "Не пришло письмо? Отправим ещё раз"
 *   sending          — "Отправляем..."
 *   success          — generic confirmation (always shown after 200, no enumeration)
 *   cooldown         — local 60s countdown after success, prevents button mashing
 *   hourly_limit_hit — when backend returned 429 (per-IP cap)
 *   error            — network/server fallback
 *
 * The component does NOT know whether the email actually exists in the DB.
 * It treats any 200 as "the request was accepted" — the backend silently
 * skips non-existing/already-verified emails. This is intentional
 * (DevSecOps requirement, OWASP ASVS 2.1.10 + 152-ФЗ).
 */
import { useEffect, useRef, useState } from 'react'
import { authApi } from '@/api/auth'
import { ApiError } from '@/api/client'
import { Icon } from '@/components/icons/Icon'

const COOLDOWN_SECONDS = 60

type State =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'success' }
  | { kind: 'cooldown'; secondsLeft: number }
  | { kind: 'hourly_limit_hit' }
  | { kind: 'error' }

export function ResendVerificationButton({
  email,
  variant = 'inline',
}: {
  email: string
  variant?: 'inline' | 'block'
}) {
  const [state, setState] = useState<State>({ kind: 'idle' })
  const intervalRef = useRef<number | null>(null)

  // Cleanup countdown on unmount.
  useEffect(() => () => {
    if (intervalRef.current) window.clearInterval(intervalRef.current)
  }, [])

  const startCooldown = () => {
    setState({ kind: 'cooldown', secondsLeft: COOLDOWN_SECONDS })
    intervalRef.current = window.setInterval(() => {
      setState((prev) => {
        if (prev.kind !== 'cooldown') return prev
        const next = prev.secondsLeft - 1
        if (next <= 0) {
          if (intervalRef.current) window.clearInterval(intervalRef.current)
          intervalRef.current = null
          return { kind: 'idle' }
        }
        return { kind: 'cooldown', secondsLeft: next }
      })
    }, 1000)
  }

  const onClick = async () => {
    if (state.kind === 'sending' || state.kind === 'cooldown' || state.kind === 'hourly_limit_hit') return
    if (!email) return
    setState({ kind: 'sending' })
    try {
      await authApi.resendVerification(email)
      setState({ kind: 'success' })
      // Show success briefly, then drop into cooldown.
      window.setTimeout(startCooldown, 2500)
    } catch (e) {
      const err = e as ApiError
      if (err.status === 429) setState({ kind: 'hourly_limit_hit' })
      else setState({ kind: 'error' })
    }
  }

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  // Render
  const isInline = variant === 'inline'
  const wrapperCls = isInline
    ? 'inline-flex items-center gap-2'
    : 'mt-3 p-3 rounded-lg bg-surface-subtle border border-border text-sm'

  const linkCls =
    'text-text-link font-semibold hover:underline disabled:opacity-60 disabled:cursor-not-allowed'

  if (state.kind === 'success') {
    return (
      <div className={wrapperCls}>
        <span role="status" className="inline-flex items-start gap-1.5 text-status-success-text text-sm">
          <Icon name="check-circle" size={16} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
          <span>Готово. Письмо в пути — обычно приходит за 1-2 минуты. Если не пришло за 5 минут — проверь спам или попробуй ещё раз</span>
        </span>
      </div>
    )
  }

  if (state.kind === 'cooldown') {
    return (
      <div className={wrapperCls}>
        <span className="text-text-secondary text-sm">
          Подожди {formatTime(state.secondsLeft)} — мы рассылаем не чаще раза в минуту, чтобы не
          спамить тебя
        </span>
      </div>
    )
  }

  if (state.kind === 'hourly_limit_hit') {
    return (
      <div className={wrapperCls}>
        <span className="text-status-warning-text text-sm">
          Лимит на отправку исчерпан. Попробуй через час или напиши на{' '}
          <a href="mailto:support@agentceo.ru" className={linkCls}>
            support@agentceo.ru
          </a>
        </span>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className={wrapperCls}>
        <span className="text-status-danger-text text-sm">
          Не получилось. Попробуй через пару минут — если не выйдет, напиши на{' '}
          <a href="mailto:support@agentceo.ru" className={linkCls}>
            support@agentceo.ru
          </a>
        </span>
        <button onClick={() => setState({ kind: 'idle' })} className={linkCls + ' ml-2 text-sm'}>
          Повторить
        </button>
      </div>
    )
  }

  // idle | sending
  return (
    <div className={wrapperCls}>
      <button
        type="button"
        onClick={onClick}
        disabled={state.kind === 'sending'}
        className={linkCls + ' text-sm'}
      >
        {state.kind === 'sending' ? 'Отправляем...' : 'Не пришло письмо? Отправим ещё раз'}
      </button>
    </div>
  )
}
